import { clearError, navigateTo, showError, useNuxtApp, useRoute } from '#imports'
import { Lock, SearchX } from '@lucide/vue'
import { useErrorStore } from '@/stores/useErrorStore'
import { useUserStore } from '@/stores/useUserStore'
import { useIdentity } from '@/composables/useIdentity'
import { useHttpErrorRecovery } from '@/composables/useHttpErrorRecovery'
import { usePresentationTranslator } from '@/composables/usePresentationTranslator'
import { AppProblemModel } from '@/services/api/AppProblemModel'
import { sanctumTokenStorage } from '@/utils/sanctumTokenStorage'
import { pendingTwoFactorChallenge } from '@/utils/pendingTwoFactorChallenge'

interface ResourceNotFoundOptions {
  title?: string
  message?: string
}

/**
 * Centralized error response handler
 * Handles auth, permission, and not-found responses globally.
 */
export const handleHttpError = async (error: unknown): Promise<void> => {
  const t = usePresentationTranslator()
  const problem = AppProblemModel.normalize(error)
  const errorStore = useErrorStore()
  const { logout } = useIdentity()
  const userStore = useUserStore()
  const route = useRoute()
  const { isRecoveringFromHttpError } = useHttpErrorRecovery()

  if (import.meta.client && isRecoveringFromHttpError.value) {
    return
  }

  if (problem.code === 'AUTHENTICATION_REQUIRED' || problem.status === 401) {
    errorStore.clearError()
    try {
      await clearError()
      try {
        await logout()
      } catch {
        // logout() throws once isAuthenticated is already false -- the vendor's own
        // 401 interceptor nulls the user before this runs -- so it never reaches its
        // own token-clearing step. Clear the token directly below regardless.
      }
    } finally {
      try {
        await sanctumTokenStorage.set(useNuxtApp(), undefined)
      } catch {
        // Best-effort: the redirect below must still happen even if this fails.
      }
      // An abandoned 2FA handoff from an earlier, uncompleted challenge must not
      // outlive this session -- otherwise a still-valid challenge_token could
      // complete a stale login on the guest-only two-factor-verification route
      // after this account has already been logged out.
      pendingTwoFactorChallenge.clear()
      userStore.clearUserState()
      await navigateTo('/login', { replace: true })
    }
    return
  } else if (problem.code === 'AUTHORIZATION_DENIED' || problem.status === 403) {
    errorStore.setError({
      type: '403',
      title: 'Access Denied',
      message: () => t('common.errorPage.actionAccessDeniedMessage'),
      icon: Lock,
      primaryAction: {
        label: 'Go to Dashboard',
        onClick: () => {
          errorStore.clearError()
          Promise.resolve(navigateTo('/dashboard')).catch(() => undefined)
        }
      },
      secondaryAction: {
        label: 'Go Back',
        onClick: () => {
          errorStore.clearError()
          window.history.back()
        }
      }
    })

    showError({
      statusCode: 403,
      fatal: false
    })
  } else if (problem.code === 'RESOURCE_NOT_FOUND' || problem.status === 404) {
    if (route.path.startsWith('/dashboard')) {
      showDashboardNotFoundError()
      return
    }

    showResourceNotFoundError()
  }
}

const showResourceNotFoundError = (options: ResourceNotFoundOptions = {}) => {
  const errorStore = useErrorStore()
  const { startHttpErrorRecovery } = useHttpErrorRecovery()

  errorStore.setError({
    type: '404',
    title: options.title ?? 'Resource Not Found',
    message: () =>
      options.message ?? 'The resource you are looking for does not exist or was deleted.',
    icon: SearchX,
    primaryAction: {
      label: 'Go to Home',
      onClick: () => {
        startHttpErrorRecovery()
        clearError({ redirect: '/' }).catch(() => undefined)
      }
    }
  })

  showError({
    statusCode: 404,
    fatal: false
  })
}

const showDashboardNotFoundError = (options: ResourceNotFoundOptions = {}) => {
  const errorStore = useErrorStore()
  const { startHttpErrorRecovery } = useHttpErrorRecovery()

  errorStore.setError({
    type: '404',
    title: options.title ?? 'Page Not Found',
    message: () =>
      options.message ?? 'The page you are looking for does not exist or has been moved.',
    icon: SearchX,
    primaryAction: {
      label: 'Go to Dashboard',
      onClick: () => {
        startHttpErrorRecovery()
        Promise.resolve(navigateTo('/dashboard', { replace: true })).catch(() => undefined)
      }
    }
  })
}
